import { useLocalization } from '@optimus-web/localization';
import {
    buildAccessibleLabel,
    useCharacterCountDebouncing,
    buildAccessibilityMarkup,
    buildCharacterCountContent,
} from '../Field/fieldAccessibilityUtils';
import type { InputProps } from './Input';

type AccessibilityProps = Pick<
    InputProps,
    'name' | 'label' | 'prefix' | 'suffix' | 'helper' | 'error' | 'maxCharacterCount' | 'value'
> & {
    accessibleLabelExtraInfo?: React.ReactNode;
};

interface AccessibilityResult {
    accessibleLabelId: string;
    accessibleDescriptionId: string | undefined;
    debouncedCharacterCount: number;
    accessibilityMarkup: React.ReactNode;
}

export const useInputAccessibility = ({
    name,
    label,
    prefix,
    suffix,
    helper,
    error,
    maxCharacterCount,
    value,
    accessibleLabelExtraInfo,
}: AccessibilityProps): AccessibilityResult => {
    const { t } = useLocalization();

    const debouncedCharacterCount = useCharacterCountDebouncing(value);

    const accessibleLabelId = `${name}-sr-only-label`;
    const accessibleLabelContent = buildAccessibleLabel([label, prefix, suffix, accessibleLabelExtraInfo]);

    const accessibleDescriptionId = helper || error || maxCharacterCount ? `${name}-sr-only-description` : undefined;
    const accessibleMaxCharacterCountDescriptionContent = buildCharacterCountContent(
        maxCharacterCount,
        debouncedCharacterCount,
        t
    );

    const accessibleDescriptionContent = buildAccessibleLabel([
        helper,
        error,
        accessibleMaxCharacterCountDescriptionContent,
    ]);

    const accessibilityMarkup = buildAccessibilityMarkup(
        accessibleLabelId,
        accessibleLabelContent,
        accessibleDescriptionId,
        accessibleDescriptionContent,
        accessibleMaxCharacterCountDescriptionContent
    );

    return {
        accessibleLabelId,
        accessibleDescriptionId,
        debouncedCharacterCount,
        accessibilityMarkup,
    };
};
